import { IFlightType, IHighlightFlightSettings, IUserAppSettings } from "@vmosolutions/oc-interfaces";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { FormikSelectField, FormikTextField } from "src/components/FormikComponents/FormikWrappers";
//material UI
import { Box, Button, Grid, Slider, Theme, Typography, useTheme } from "@mui/material";
// API calls
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { IconButton } from "src/components/IconButton/IconButton";
import { returnSelectedOptions } from "src/components/SearchFilters/Helpers";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IDispatchDeskExpanded } from "src/redux/reducers/DispatchDeskReducer";
import { IAirportOption } from "src/redux/reducers/StructuresReducer";
import { airportSearchString, updateAirportOptions } from "src/utils/airportHelper";

interface ITimelineFlightHighlight {
  children?: any;
  setTabValue?: React.Dispatch<React.SetStateAction<string | boolean>>;
  groupSave?: boolean;
}

const TimelineFlightHighlightStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  alignItems: "center",
  "& .flightNumber": {
    width: 180,
    "& .label": { width: "auto" },
  },
  "& .sim_switch": {
    margin: "5px",
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "2px",
    width: "auto",
    height: 24,
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 300,
  },
  "& .location_switch": {
    display: "flex",
    "& .switch": {
      height: 24,
      "&.focused label": { color: "unset" },
      "& label": {
        margin: 0,
        width: 40,
      },
      "& .switch-buttons": {
        "& button": { padding: "0px 8px" },
      },
    },
  },
  "& .dimmSetting": {
    height: 24,
    width: "auto",
    alignItems: "center",
    marginLeft: "5px",
    "&.dirty": {
      "& .label": { color: theme.custom.forms.dirtyColor },
      "& .slider": {
        border: `1px solid ${theme.custom.forms.dirtyColor}`,
      },
    },
    "& .label": { fontSize: 12, marginRight: "10px" },
    "& .slider": {
      margin: "5px",
      padding: 0,
      width: 100,
      height: 22,
      overflow: "hidden",
      border: `1px solid ${theme.custom.forms.inputBorder}`,
      "& .MuiSlider-rail": {
        height: 20,
        backgroundColor: theme.custom.forms.inputBack,
        opacity: 1,
      },
      "& .MuiSlider-track": {
        height: 22,
      },
      "& .MuiSlider-thumb": {
        visibility: "hidden",
      },
    },
  },
});

const defaultSettings: IHighlightFlightSettings = {
  active: false,
  dimmSetting: 0.1,
  conditions: {
    flightNumber: undefined,
    origin: [],
    destination: [],
    flightType: [],
    dispatchDesk: [],
    locationInclusive: true,
    numberInclusive: false,
  },
  isSaved: false,
};

type HighlightFlightForm = FormikProps<IHighlightFlightSettings>;
/**
 * Will return true if any of the 3 supplied conditions are assigned a value.
 * @param payload -
 * @returns
 */
const containsValidConditions = (payload: IHighlightFlightSettings): boolean => {
  if (!payload?.conditions) {
    return false;
  }
  const { conditions } = payload;
  const hasOrigin = conditions?.origin?.length > 0;
  const hasDestination = conditions?.destination?.length > 0;
  const hasValidFlightNumber = conditions?.flightNumber?.trim()?.length > 0;
  const hasValidFlightType = conditions?.flightType?.length > 0;
  const hasDispatchDesk = conditions?.dispatchDesk?.length > 0;
  const hasHighlightUnassigned = !!conditions?.highlightUnassigned;
  return (
    hasOrigin ||
    hasDestination ||
    hasValidFlightNumber ||
    hasValidFlightType ||
    hasDispatchDesk ||
    hasHighlightUnassigned
  );
};

/**
 * TIMELINE Flight Highlight UI
 */
export const TimelineFlightHighlight = forwardRef((props: ITimelineFlightHighlight, ref: any): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setTabValue, groupSave } = props;

  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const appSettings: IUserAppSettings | null = useSelector((state: IRootState) => state.authentication.appSettings);
  const initialFormValues = { ...defaultSettings, ...appSettings?.highlightFlightSettings };
  const flightTypes = useSelector((state: IRootState) => state.structures.customerStructure.flightTypes);
  const dispatchDesks = useSelector((state: IRootState) => state.dispatchDesks.desks);
  const formikRef = useRef();
  const handleSubmit = useCallback(
    async (values: IHighlightFlightSettings, helpers: FormikHelpers<IHighlightFlightSettings>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      setSubmitting(true);

      const { lastApplied, _deskFlightIds, _allDeskFlightIds, ...payload } = values; // strip out runtime-only fields before saving.
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Saving Highlight Settings...",
        successMessage: "Highlight Settings Successfully Saved and Applied.",
        errorMessage: "Error: Unable to Save Highlight Settings.",
      };

      const payloadHasConditions = containsValidConditions(payload);

      const response = await AmplifyAPIWrapper.Put<IUserAppSettings>(
        "/me/settings",
        {
          highlightFlightSettings: payloadHasConditions ? { ...payload, isSaved: true } : defaultSettings, // set isSaved as true.
        },
        !groupSave && notifications
      );
      if (response.highlightFlightSettings) {
        resetForm({ values: response?.highlightFlightSettings });
        setStatus({ success: true });
        // Once confirmed as saved, apply the new settings and add lastApplied info (to reduce buffer redraws).
        const updatedAppSettings = {
          ...response,
          highlightFlightSettings: { ...response?.highlightFlightSettings, lastApplied: new Date()?.valueOf() },
        };
        dispatch(updateUserAppSettings(updatedAppSettings, "highlightFlightSettings"));
      } else {
        setStatus({ success: false });
      }

      setSubmitting(false);
    },
    [dispatch, groupSave]
  );

  /** Restores default saved settings */
  const handleRestoreDefault = useCallback(async (): Promise<void> => {
    const response = await AmplifyAPIWrapper.Put<IUserAppSettings>("/me/settings", {}, null);
    if (response?.highlightFlightSettings) {
      // Once confirmed as saved, apply the new settings and add lastApplied info (to reduce buffer redraws).
      const updatedAppSettings = {
        ...response,
        highlightFlightSettings: { ...response?.highlightFlightSettings, lastApplied: new Date()?.valueOf() },
      };
      dispatch(updateUserAppSettings(updatedAppSettings, "highlightFlightSettings"));
    }
  }, [dispatch]);

  const handleFlightNumberConfirm = (
    e: React.KeyboardEvent<HTMLDivElement>,
    values: IHighlightFlightSettings,
    dirty: boolean
  ): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApplySettings({ ...values, active: !initialFormValues?.active || dirty });
    }
  };

  /** Apply these settings locally - does not save them */
  const handleApplySettings = useCallback(
    (values: IHighlightFlightSettings): void => {
      const valuesHaveConditions = containsValidConditions(values);
      const updatedAppSettings = {
        ...appSettings,
        highlightFlightSettings: valuesHaveConditions
          ? { ...values, lastApplied: new Date()?.valueOf() }
          : { ...defaultSettings, lastApplied: new Date()?.valueOf() },
      };
      dispatch(updateUserAppSettings(updatedAppSettings, "highlightFlightSettings"));
    },
    [dispatch, appSettings]
  );

  useImperativeHandle(ref, () => ({
    SaveDefault() {
      if (formikRef?.current) {
        //@ts-ignore
        return formikRef.current.values;
      }
    },
  }));

  return (
    <Box sx={TimelineFlightHighlightStyles(theme)}>
      {!!setTabValue && (
        <IconButton style={{ paddingTop: "3px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
      )}
      <Typography variant="subtitle1" className="body_text">
        Highlight Timeline Flights
      </Typography>
      <Typography variant="body2" style={{ margin: "0px 8px" }}>
        Flights that match applied condition will appear normal. All other flights will be dimmed to applied opacity.
      </Typography>
      <Grid container>
        <Formik
          initialValues={initialFormValues}
          innerRef={formikRef}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {(highlightSettingForm: HighlightFlightForm) => {
            const { initialValues, values, dirty, isSubmitting, setFieldValue, setFieldTouched, setValues } =
              highlightSettingForm;

            return (
              <Form style={{ ...TimelineFlightHighlightStyles(theme), width: "100%" }}>
                <Grid container className="form_body">
                  <Grid
                    container
                    alignItems="flex-start"
                    style={{
                      borderColor: theme.palette.primary.contrastText,
                      borderStyle: "solid",
                      borderWidth: "0px 1px 1px 1px",
                      margin: "10px 10px 0px 0px",
                      width: "auto",
                      marginTop: 10,
                    }}
                  >
                    <Grid item style={{ width: 250 }}>
                      <FormikSelectField
                        className="auto_width filter airport_selection"
                        label="Origin"
                        name="conditions.origin"
                        format={(value) => returnSelectedOptions(Object.values(airportOptions), value, "ident")}
                        parse={(airports: IAirportOption[] | IAirportOption) => {
                          if (Array?.isArray(airports)) {
                            const identArray = airports?.map((airport) => airport.ident);
                            return identArray;
                          } else {
                            return (airports as IAirportOption)?.ident ? [(airports as IAirportOption)?.ident] : [];
                          }
                        }}
                        onChange={(value: any) => {
                          // If user assigns a filter value and active state if false, set it true
                          if ((value?.length > 0 || !!value) && !values?.active) {
                            setFieldValue("active", true);
                            setFieldTouched("active", true);
                            setFieldValue("isSaved", false);
                            setFieldTouched("isSaved", true);
                          }
                        }}
                        isMulti={values?.conditions?.locationInclusive ? false : true}
                        onInputChange={(ident: string) => {
                          if (ident?.length >= 2) {
                            // Only run a search if 2 or more characters exist in search string.
                            updateAirportOptions(ident, airportOptions, dispatch);
                          }
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) =>
                          airport ? `${airport.ident}/${airport.iata} - ${airport.name}` : ""
                        }
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        noOptionsMessage={() => airportSearchString}
                        isSearchable={true}
                        isClearable={true}
                        menuPosition="fixed"
                        maxMenuHeight={400}
                        // menuPortalTarget={parentElement}
                        openMenuOnFocus={false}
                        blurInputOnSelect={true}
                        closeMenuOnSelect={true}
                        placeholder=""
                      />
                    </Grid>

                    <div className="location_switch">
                      <SwitchField
                        className="switch"
                        name="conditions.locationInclusive"
                        value={values?.conditions?.locationInclusive ?? false}
                        onChange={() => {
                          const newValue = !values?.conditions?.locationInclusive;
                          setFieldValue("conditions.locationInclusive", newValue);
                          setFieldTouched("conditions.locationInclusive", true);
                          setFieldValue("isSaved", false);
                          setFieldTouched("isSaved", true);
                        }}
                        switchlabels={["AND", "OR"]}
                        switchtooltips={["", ""]}
                        label=""
                        allowNull={false}
                      />
                    </div>

                    <Grid item style={{ width: 280 }}>
                      <FormikSelectField
                        className="auto_width filter airport_selection"
                        label="Destination"
                        name="conditions.destination"
                        format={(value) => returnSelectedOptions(Object.values(airportOptions), value, "ident")}
                        parse={(airports: IAirportOption[] | IAirportOption) => {
                          if (Array?.isArray(airports)) {
                            const identArray = airports?.map((airport) => airport.ident);
                            return identArray;
                          } else {
                            return (airports as IAirportOption)?.ident ? [(airports as IAirportOption)?.ident] : [];
                          }
                        }}
                        onChange={(value: any) => {
                          // If user assigns a filter value and active state if false, set it true
                          if ((value?.length > 0 || !!value) && !values?.active) {
                            setFieldValue("active", true);
                            setFieldTouched("active", true);
                            setFieldValue("isSaved", false);
                            setFieldTouched("isSaved", true);
                          }
                        }}
                        isMulti={values?.conditions?.locationInclusive ? false : true}
                        onInputChange={(ident: string) => {
                          if (ident?.length >= 2) {
                            // Only run a search if 2 or more characters exist in search string.
                            updateAirportOptions(ident, airportOptions, dispatch);
                          }
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) =>
                          airport ? `${airport.ident}/${airport.iata} - ${airport.name}` : ""
                        }
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        noOptionsMessage={() => airportSearchString}
                        isSearchable={true}
                        isClearable={true}
                        menuPosition="fixed"
                        maxMenuHeight={400}
                        // menuPortalTarget={parentElement}
                        openMenuOnFocus={false}
                        blurInputOnSelect={true}
                        closeMenuOnSelect={true}
                        placeholder=""
                      />
                    </Grid>
                  </Grid>

                  <Grid container style={{ width: "auto", margin: "10px 10px 0px 0px" }}>
                    <Typography className="sim_switch">AND</Typography>
                    <Grid
                      item
                      style={{
                        borderColor: theme.palette.primary.contrastText,
                        borderStyle: "solid",
                        borderWidth: "0px 1px 1px 1px",
                        margin: "0px 10px",
                      }}
                    >
                      <FormikSelectField
                        className="filter airport_selection"
                        label="Flight Type"
                        name="conditions.flightType"
                        isMulti={true}
                        format={(val) => returnSelectedOptions(Object.values(flightTypes), val, "_id")}
                        parse={(flightTypes: IFlightType[] | IFlightType) => {
                          if (Array?.isArray(flightTypes)) {
                            const descArray = flightTypes.map((flightType) => flightType._id);
                            return descArray;
                          } else {
                            return flightTypes ? [(flightTypes as IFlightType)?._id] : [];
                          }
                        }}
                        options={Object.values(flightTypes)}
                        getOptionLabel={(flightType: IFlightType) =>
                          flightType ? `${flightType.title} - ${flightType.description}` : ""
                        }
                        getOptionValue={(flightType: IFlightType) => flightType.description}
                        isSearchable={true}
                        isClearable={true}
                        menuPosition="fixed"
                        maxMenuHeight={400}
                        openMenuOnFocus={false}
                        blurInputOnSelect={true}
                        closeMenuOnSelect={false}
                        placeholder=""
                      />
                    </Grid>
                  </Grid>

                  <Grid container style={{ width: "auto", margin: "10px 10px 0px 0px" }}>
                    <Typography className="sim_switch">AND</Typography>
                    <Grid
                      item
                      style={{
                        borderColor: theme.palette.primary.contrastText,
                        borderStyle: "solid",
                        borderWidth: "0px 1px 1px 1px",
                        margin: "0px 10px",
                      }}
                    >
                      <FormikTextField
                        className="flightNumber"
                        label="Flight #"
                        name="conditions.flightNumber"
                        value={values?.conditions?.flightNumber}
                        onKeyDown={(e) => handleFlightNumberConfirm(e, values, dirty)}
                        onChange={(event: any) => {
                          if ((event?.target?.value?.length > 0 || !!event?.target?.value) && !values?.active) {
                            setFieldValue("active", true);
                            setFieldTouched("active", true);
                            setFieldValue("isSaved", false);
                            setFieldTouched("isSaved", true);
                          }
                        }}
                        placeholder="example: 588,222,200-240"
                      />
                    </Grid>
                  </Grid>

                  {dispatchDesks?.length > 0 && (
                    <Grid container style={{ width: "auto", margin: "10px 10px 0px 0px" }}>
                      <Typography className="sim_switch">AND</Typography>
                      <Grid
                        item
                        style={{
                          borderColor: theme.palette.primary.contrastText,
                          borderStyle: "solid",
                          borderWidth: "0px 1px 1px 1px",
                          margin: "0px 10px",
                        }}
                      >
                        <FormikSelectField
                          className="filter airport_selection"
                          label="Dispatch Desk"
                          name="conditions.dispatchDesk"
                          isMulti={true}
                          isDisabled={!!values?.conditions?.highlightUnassigned}
                          format={(val: string[]) => returnSelectedOptions(dispatchDesks, val, "_id")}
                          parse={(desks: IDispatchDeskExpanded[] | IDispatchDeskExpanded) => {
                            if (Array?.isArray(desks)) {
                              return desks.map((desk) => desk._id);
                            } else {
                              return desks ? [(desks as IDispatchDeskExpanded)?._id] : [];
                            }
                          }}
                          onChange={(value: any) => {
                            if ((value?.length > 0 || !!value) && !values?.active) {
                              setFieldValue("active", true);
                              setFieldTouched("active", true);
                              setFieldValue("isSaved", false);
                              setFieldTouched("isSaved", true);
                            }
                          }}
                          options={dispatchDesks}
                          getOptionLabel={(desk: IDispatchDeskExpanded) => (desk ? desk.name : "")}
                          getOptionValue={(desk: IDispatchDeskExpanded) => desk._id}
                          isSearchable={true}
                          isClearable={true}
                          menuPosition="fixed"
                          maxMenuHeight={400}
                          openMenuOnFocus={false}
                          blurInputOnSelect={true}
                          closeMenuOnSelect={false}
                          placeholder=""
                        />
                      </Grid>
                      <div className="location_switch">
                        <SwitchField
                          className="switch"
                          name="conditions.highlightUnassigned"
                          value={values?.conditions?.highlightUnassigned ?? false}
                          onChange={() => {
                            const newValue = !values?.conditions?.highlightUnassigned;
                            setFieldValue("conditions.highlightUnassigned", newValue);
                            setFieldTouched("conditions.highlightUnassigned", true);
                            if (newValue) {
                              // Clear specific desk selection when switching to unassigned mode
                              setFieldValue("conditions.dispatchDesk", []);
                              setFieldTouched("conditions.dispatchDesk", true);
                            }
                            if (!values?.active) {
                              setFieldValue("active", true);
                              setFieldTouched("active", true);
                            }
                            setFieldValue("isSaved", false);
                            setFieldTouched("isSaved", true);
                          }}
                          switchlabels={["Unassigned", "All Desks"]}
                          switchtooltips={[
                            "Highlight flights not assigned to any desk",
                            "Switch to specific desk selection",
                          ]}
                          label=""
                          allowNull={false}
                        />
                      </div>
                    </Grid>
                  )}

                  <Grid container style={{ width: "auto", margin: "10px 10px 0px 0px" }}>
                    <Grid
                      container
                      style={{ width: "auto" }}
                      className={`dimmSetting ${initialValues?.dimmSetting !== values?.dimmSetting ? "dirty" : ""}`}
                    >
                      <Typography variant="body2" className="label">
                        Opacity
                      </Typography>
                      <Slider
                        name="dimmSetting"
                        className="slider"
                        valueLabelDisplay="auto"
                        aria-label="Opacity"
                        // defaultValue={values?.dimmSetting}
                        // marks={marks}
                        step={0.1}
                        min={0.0}
                        max={0.6}
                        value={values?.dimmSetting}
                        onChange={(event: any, value) => {
                          setFieldValue("dimmSetting", value);
                          setFieldTouched("dimmSetting", true);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {!groupSave && (
                  <Grid container className="form_footer" alignItems="center" style={{ marginTop: 10 }}>
                    <Button
                      className="btn reset"
                      variant="contained"
                      color="secondary"
                      disabled={values.isSaved}
                      onClick={debounce(handleRestoreDefault, 500)}
                    >
                      Restore Default
                    </Button>
                    <Button
                      className={"btn save"}
                      variant="contained"
                      color={initialFormValues?.active && !dirty ? "secondary" : "primary"}
                      onClick={() => handleApplySettings({ ...values, active: !initialFormValues?.active || dirty })}
                    >
                      {initialFormValues?.active && !dirty ? "Disable Highlight" : "Apply Highlight"}
                    </Button>

                    <Button id={"save_highlight_settings_btn"} variant="outlined" disabled={isSubmitting} type="submit">
                      Save as default
                    </Button>
                  </Grid>
                )}
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Box>
  );
});
